import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ASSETS } from '../data/assets';
import {
  createDialogueLine,
  createEmptyDialogue,
  createGuestAvatar,
  defaultDurationMs,
  ensureDialogue,
  mainAvatar,
  patchLineText,
  resolveHudStyle,
} from '../lib/dialogue';
import {
  createMediaCue,
  mediaCuesOf,
  pruneMediaCues,
  upsertMediaCue,
} from '../lib/dialogueMediaCues';
import { canAddObjectOnTop, canStackObject } from '../lib/objectHierarchy';
import { getRoomPolygon, getRoomWalls, pointInPolygon } from '../lib/roomGeometry';
import type {
  AssetDefinition,
  DialogueAvatar,
  DialogueCameraCue,
  DialogueHudStyle,
  DialogueLine,
  DialogueMediaCue,
  DialogueScript,
  MediaCueWhen,
  MediaRef,
  PlacedObject,
  ProjectSnapshot,
  RoomConfig,
  ToolMode,
  User,
} from '../types';

const STORAGE_KEY = 'expospace.project.v1';
const LEGACY_STORAGE_KEY = 'baechisil.project.v1';
const DEFAULT_ROOM: RoomConfig = {
  width: 6,
  depth: 4,
  height: 2.8,
  wallNorth: true,
  wallWest: true,
  floorColor: '#d9d7cf',
};

const STARTER_OBJECTS: PlacedObject[] = [
  makeObject(ASSETS[1], [-0.8, 0, -0.25]),
  makeObject(ASSETS[2], [1.55, 0, -1.15]),
  makeObject(ASSETS[0], [-2.2, 0, -1.25]),
];

function makeObject(asset: AssetDefinition, position: [number, number, number]): PlacedObject {
  return {
    id: crypto.randomUUID(),
    kind: asset.kind,
    name: asset.name,
    position,
    rotation: 0,
    color: asset.defaultColor,
    dimensions: asset.dimensions,
    cover: asset.kind === 'table' ? 'none' : undefined,
  };
}

function stripSessionMedia(dialogue: DialogueScript | undefined): DialogueScript | undefined {
  if (!dialogue) return undefined;
  const stripRef = (ref: MediaRef | undefined): MediaRef | undefined => {
    if (!ref) return undefined;
    const { blobUrl: _blobUrl, ...rest } = ref;
    return rest;
  };
  return {
    avatars: dialogue.avatars.map((avatar) => ({
      ...avatar,
      image: stripRef(avatar.image) ?? avatar.image,
    })),
    lines: dialogue.lines.map((line) => ({
      ...line,
      voice: stripRef(line.voice),
    })),
    hudStyle: dialogue.hudStyle,
    mediaCues: dialogue.mediaCues ?? [],
  };
}

function normalizeProject(stored: ProjectSnapshot): ProjectSnapshot {
  return {
    ...stored,
    objects: stored.objects.map(({ mediaUrl: _mediaUrl, mediaName: _mediaName, ...object }) => object),
    dialogue: ensureDialogue(stored.dialogue),
    schemaVersion: 2,
  };
}

function loadProject(): ProjectSnapshot {
  try {
    const value = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    if (value) {
      return normalizeProject(JSON.parse(value) as ProjectSnapshot);
    }
  } catch {
    // Storage may be unavailable in privacy mode. The editor still works in memory.
  }
  return {
    name: 'COEX 봄 전시 부스',
    room: DEFAULT_ROOM,
    objects: STARTER_OBJECTS,
    dialogue: createEmptyDialogue(),
    updatedAt: new Date().toISOString(),
    schemaVersion: 2,
  };
}

export function useStudio() {
  const [project, setProject] = useState<ProjectSnapshot>(loadProject);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tool, setTool] = useState<ToolMode>('select');
  const [user, setUser] = useState<User | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'dirty'>('saved');
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setSaveState('saving');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        const localSnapshot = {
          ...project,
          updatedAt: new Date().toISOString(),
          schemaVersion: 2 as const,
          objects: project.objects.map(({ mediaUrl: _mediaUrl, mediaName: _mediaName, ...object }) => object),
          dialogue: stripSessionMedia(project.dialogue),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(localSnapshot));
      } finally {
        setSaveState('saved');
      }
    }, 450);
    return () => clearTimeout(saveTimer.current);
  }, [project]);

  const selected = useMemo(
    () => project.objects.find((object) => object.id === selectedId) ?? null,
    [project.objects, selectedId],
  );

  const patchProject = useCallback((patch: Partial<ProjectSnapshot>) => {
    setSaveState('dirty');
    setProject((previous) => ({ ...previous, ...patch, updatedAt: new Date().toISOString() }));
  }, []);

  const addObject = useCallback((asset: AssetDefinition, position?: [number, number, number]) => {
    const slot = project.objects.length;
    const fallback: [number, number, number] = [
      Math.max(-project.room.width / 2 + 0.6, Math.min(project.room.width / 2 - 0.6, ((slot % 3) - 1) * 1.05)),
      0,
      Math.max(-project.room.depth / 2 + 0.6, Math.min(project.room.depth / 2 - 0.6, Math.floor(slot / 3) * 0.75)),
    ];
    const desired = position ?? fallback;
    const polygon = getRoomPolygon(project.room);
    const safePosition: [number, number, number] = pointInPolygon(desired[0], desired[2], polygon) ? desired : [0, desired[1], 0];
    const object = makeObject(asset, safePosition);
    if (asset.kind === 'scroll-banner') {
      const walls = getRoomWalls(project.room);
      const segmentIndex = Math.max(0, walls.findIndex((wall) => wall.enabled));
      object.mountMode = 'wall';
      object.wallMount = {
        segmentIndex,
        offset: 0,
        height: asset.dimensions.height / 2 + 0.2,
      };
    }
    setProject((previous) => ({ ...previous, objects: [...previous.objects, object] }));
    setSelectedId(object.id);
    return object;
  }, [project.objects.length, project.room.depth, project.room.width]);

  const addCustomObject = useCallback((name: string, modelUrl: string) => {
    const object: PlacedObject = {
      id: crypto.randomUUID(),
      kind: 'custom',
      name,
      position: [0, 0, 0],
      rotation: 0,
      color: '#aeb7b0',
      dimensions: { width: 1, height: 1, depth: 1 },
      modelUrl,
    };
    setProject((previous) => ({ ...previous, objects: [...previous.objects, object] }));
    setSelectedId(object.id);
  }, []);

  const addObjectOnTop = useCallback((asset: AssetDefinition, supportId: string, topY?: number) => {
    const support = project.objects.find((object) => object.id === supportId);
    if (!support || !canAddObjectOnTop(project.objects, supportId)) return null;
    const object = makeObject(asset, [
      support.position[0],
      topY ?? support.position[1] + support.dimensions.height,
      support.position[2],
    ]);
    object.supportId = supportId;
    setProject((previous) => ({ ...previous, objects: [...previous.objects, object] }));
    setSelectedId(object.id);
    return object;
  }, [project.objects]);

  const stackExistingObject = useCallback((id: string, supportId: string) => {
    setProject((previous) => {
      const source = previous.objects.find((object) => object.id === id);
      const support = previous.objects.find((object) => object.id === supportId);
      if (!source || !support || !canStackObject(previous.objects, id, supportId)) return previous;

      const descendants = new Set<string>();
      let changed = true;
      while (changed) {
        changed = false;
        previous.objects.forEach((object) => {
          if (object.supportId && (object.supportId === id || descendants.has(object.supportId)) && !descendants.has(object.id)) {
            descendants.add(object.id);
            changed = true;
          }
        });
      }
      const nextPosition: [number, number, number] = [
        support.position[0],
        support.position[1] + support.dimensions.height,
        support.position[2],
      ];
      const delta: [number, number, number] = [
        nextPosition[0] - source.position[0],
        nextPosition[1] - source.position[1],
        nextPosition[2] - source.position[2],
      ];
      return {
        ...previous,
        objects: previous.objects.map((object) => {
          if (object.id === id) return { ...object, supportId, position: nextPosition };
          if (descendants.has(object.id)) {
            return { ...object, position: [object.position[0] + delta[0], object.position[1] + delta[1], object.position[2] + delta[2]] };
          }
          return object;
        }),
      };
    });
    setSelectedId(id);
  }, []);

  const canAddOnTop = useCallback(
    (supportId: string) => canAddObjectOnTop(project.objects, supportId),
    [project.objects],
  );

  const detachObject = useCallback((id: string) => {
    setProject((previous) => {
      const source = previous.objects.find((object) => object.id === id);
      const support = source?.supportId
        ? previous.objects.find((object) => object.id === source.supportId)
        : undefined;
      if (!source || !support) return previous;

      const descendants = new Set<string>();
      let changed = true;
      while (changed) {
        changed = false;
        previous.objects.forEach((object) => {
          if (object.supportId && (object.supportId === id || descendants.has(object.supportId)) && !descendants.has(object.id)) {
            descendants.add(object.id);
            changed = true;
          }
        });
      }
      const nextPosition: [number, number, number] = [support.position[0], 0, support.position[2]];
      const delta: [number, number, number] = [
        nextPosition[0] - source.position[0],
        nextPosition[1] - source.position[1],
        nextPosition[2] - source.position[2],
      ];
      return {
        ...previous,
        objects: previous.objects.map((object) => {
          if (object.id === id) return { ...object, supportId: undefined, position: nextPosition };
          if (descendants.has(object.id)) {
            return { ...object, position: [object.position[0] + delta[0], object.position[1] + delta[1], object.position[2] + delta[2]] };
          }
          return object;
        }),
      };
    });
    setSelectedId(id);
  }, []);

  const updateObject = useCallback((id: string, patch: Partial<PlacedObject>) => {
    setProject((previous) => {
      const source = previous.objects.find((object) => object.id === id);
      if (!source) return previous;
      const nextPosition = patch.position;
      const delta = nextPosition
        ? [nextPosition[0] - source.position[0], nextPosition[1] - source.position[1], nextPosition[2] - source.position[2]] as [number, number, number]
        : null;
      const descendants = new Set<string>();
      let changed = true;
      while (changed) {
        changed = false;
        previous.objects.forEach((object) => {
          if (object.supportId && (object.supportId === id || descendants.has(object.supportId)) && !descendants.has(object.id)) {
            descendants.add(object.id);
            changed = true;
          }
        });
      }
      return {
        ...previous,
        objects: previous.objects.map((object) => {
          if (object.id === id) return { ...object, ...patch };
          if (delta && descendants.has(object.id)) {
            return { ...object, position: [object.position[0] + delta[0], object.position[1] + delta[1], object.position[2] + delta[2]] };
          }
          return object;
        }),
      };
    });
  }, []);

  const deleteObject = useCallback((id: string) => {
    setProject((previous) => {
      const removedIds = new Set([id]);
      let changed = true;
      while (changed) {
        changed = false;
        previous.objects.forEach((object) => {
          if (object.supportId && removedIds.has(object.supportId) && !removedIds.has(object.id)) {
            removedIds.add(object.id);
            changed = true;
          }
        });
      }
      previous.objects.forEach((object) => {
        if (removedIds.has(object.id) && object.mediaUrl?.startsWith('blob:')) URL.revokeObjectURL(object.mediaUrl);
      });
      const dialogue = previous.dialogue
        ? {
            ...ensureDialogue(previous.dialogue),
            mediaCues: pruneMediaCues(mediaCuesOf(ensureDialogue(previous.dialogue)), {
              objectIds: new Set(
                previous.objects.filter((object) => !removedIds.has(object.id)).map((object) => object.id),
              ),
            }),
          }
        : previous.dialogue;
      return {
        ...previous,
        objects: previous.objects.filter((object) => !removedIds.has(object.id)),
        dialogue,
      };
    });
    setSelectedId(null);
  }, []);

  const duplicateObject = useCallback((id: string) => {
    const source = project.objects.find((object) => object.id === id);
    if (!source) return;
    const duplicate = {
      ...source,
      id: crypto.randomUUID(),
      name: `${source.name} 복사본`,
      position: [source.position[0] + 0.5, source.position[1], source.position[2] + 0.5] as [number, number, number],
      viewEnabled: source.viewContent?.storage === 'local' ? false : source.viewEnabled,
      viewContent: source.viewContent?.storage === 'local' ? undefined : source.viewContent,
    };
    setProject((previous) => ({ ...previous, objects: [...previous.objects, duplicate] }));
    setSelectedId(duplicate.id);
  }, [project.objects]);

  const dialogue = useMemo(() => ensureDialogue(project.dialogue), [project.dialogue]);

  const setDialogue = useCallback((next: DialogueScript | ((previous: DialogueScript) => DialogueScript)) => {
    setSaveState('dirty');
    setProject((previous) => {
      const current = ensureDialogue(previous.dialogue);
      const dialogueNext = typeof next === 'function' ? next(current) : next;
      return { ...previous, dialogue: dialogueNext, schemaVersion: 2, updatedAt: new Date().toISOString() };
    });
  }, []);

  const updateAvatar = useCallback((id: string, patch: Partial<DialogueAvatar>) => {
    setDialogue((previous) => ({
      ...previous,
      avatars: previous.avatars.map((avatar) => (avatar.id === id ? { ...avatar, ...patch } : avatar)),
    }));
  }, [setDialogue]);

  const addGuestAvatar = useCallback(() => {
    setDialogue((previous) => {
      const guests = previous.avatars.filter((avatar) => avatar.side === 'right').length;
      return { ...previous, avatars: [...previous.avatars, createGuestAvatar(guests)] };
    });
  }, [setDialogue]);

  const removeAvatar = useCallback((id: string) => {
    setDialogue((previous) => {
      const target = previous.avatars.find((avatar) => avatar.id === id);
      if (!target || target.side === 'left') return previous;
      const fallback = mainAvatar(previous)?.id;
      return {
        avatars: previous.avatars.filter((avatar) => avatar.id !== id),
        lines: previous.lines.map((line) => (
          line.speakerId === id && fallback ? { ...line, speakerId: fallback } : line
        )),
        hudStyle: previous.hudStyle,
        mediaCues: previous.mediaCues,
      };
    });
  }, [setDialogue]);

  const addDialogueLine = useCallback((afterId?: string) => {
    setDialogue((previous) => {
      const speaker = mainAvatar(previous) ?? previous.avatars[0];
      if (!speaker) return previous;
      const line = createDialogueLine(speaker.id, '', speaker.side);
      if (!afterId) return { ...previous, lines: [...previous.lines, line] };
      const index = previous.lines.findIndex((item) => item.id === afterId);
      if (index < 0) return { ...previous, lines: [...previous.lines, line] };
      const lines = [...previous.lines];
      lines.splice(index + 1, 0, line);
      return { ...previous, lines };
    });
  }, [setDialogue]);

  const updateDialogueLine = useCallback((id: string, patch: Partial<DialogueLine>) => {
    setDialogue((previous) => ({
      ...previous,
      lines: previous.lines.map((line) => {
        if (line.id !== id) return line;
        let next = { ...line, ...patch };
        if (patch.text !== undefined) {
          const texted = patchLineText(line, patch.text);
          next = {
            ...next,
            text: texted.text,
            durationMs: patch.durationMs !== undefined || line.durationLocked
              ? (patch.durationMs ?? line.durationMs)
              : texted.durationMs,
          };
        }
        if (patch.durationMs !== undefined && patch.durationLocked === undefined) {
          next = { ...next, durationLocked: true };
        }
        return next;
      }),
    }));
  }, [setDialogue]);

  const removeDialogueLine = useCallback((id: string) => {
    setDialogue((previous) => {
      const lines = previous.lines.filter((line) => line.id !== id);
      return {
        ...previous,
        lines,
        mediaCues: pruneMediaCues(mediaCuesOf(previous), {
          lineIds: new Set(lines.map((line) => line.id)),
          objectIds: undefined,
        }),
      };
    });
  }, [setDialogue]);

  const assignMediaCue = useCallback((
    objectId: string,
    lineId: string,
    when: MediaCueWhen = 'onCut',
  ): { replaced: boolean } => {
    let replaced = false;
    setDialogue((previous) => {
      const cue = createMediaCue(objectId, lineId, when);
      const result = upsertMediaCue(mediaCuesOf(previous), cue);
      replaced = result.replaced;
      return { ...previous, mediaCues: result.cues };
    });
    return { replaced };
  }, [setDialogue]);

  const updateMediaCue = useCallback((id: string, patch: Partial<DialogueMediaCue>) => {
    setDialogue((previous) => {
      const current = mediaCuesOf(previous);
      const target = current.find((cue) => cue.id === id);
      if (!target) return previous;
      const next = { ...target, ...patch };
      if (patch.objectId && patch.objectId !== target.objectId) {
        return { ...previous, mediaCues: upsertMediaCue(current.filter((cue) => cue.id !== id), next).cues };
      }
      return {
        ...previous,
        mediaCues: current.map((cue) => (cue.id === id ? next : cue)),
      };
    });
  }, [setDialogue]);

  const removeMediaCue = useCallback((id: string) => {
    setDialogue((previous) => ({
      ...previous,
      mediaCues: mediaCuesOf(previous).filter((cue) => cue.id !== id),
    }));
  }, [setDialogue]);
  const moveDialogueLine = useCallback((id: string, direction: -1 | 1) => {
    setDialogue((previous) => {
      const index = previous.lines.findIndex((line) => line.id === id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= previous.lines.length) return previous;
      const lines = [...previous.lines];
      const [item] = lines.splice(index, 1);
      lines.splice(nextIndex, 0, item);
      return { ...previous, lines };
    });
  }, [setDialogue]);

  const setLineCamera = useCallback((id: string, camera: DialogueCameraCue | undefined) => {
    updateDialogueLine(id, { camera });
  }, [updateDialogueLine]);

  const resetLineDuration = useCallback((id: string) => {
    setDialogue((previous) => ({
      ...previous,
      lines: previous.lines.map((line) => (
        line.id === id
          ? { ...line, durationMs: defaultDurationMs(line.text), durationLocked: false }
          : line
      )),
    }));
  }, [setDialogue]);

  const updateHudStyle = useCallback((patch: Partial<DialogueHudStyle>) => {
    setDialogue((previous) => ({
      ...previous,
      hudStyle: { ...resolveHudStyle(previous.hudStyle), ...patch },
    }));
  }, [setDialogue]);

  return {
    project,
    setProject,
    patchProject,
    selected,
    selectedId,
    setSelectedId,
    tool,
    setTool,
    user,
    setUser,
    saveState,
    addObject,
    addCustomObject,
    addObjectOnTop,
    canAddOnTop,
    stackExistingObject,
    detachObject,
    updateObject,
    deleteObject,
    duplicateObject,
    dialogue,
    setDialogue,
    updateAvatar,
    addGuestAvatar,
    removeAvatar,
    addDialogueLine,
    updateDialogueLine,
    removeDialogueLine,
    moveDialogueLine,
    setLineCamera,
    resetLineDuration,
    updateHudStyle,
    assignMediaCue,
    updateMediaCue,
    removeMediaCue,
  };
}

export type StudioController = ReturnType<typeof useStudio>;
